import { UserRole } from '@common/interfaces/user'
import { ROUTES } from '../../constants/routes'
import {
  SIDE_MENU as SIDE_MENU_CLIENT,
  SIDE_MENU_CLIENT_USER,
  SIDE_MENU_DUE_DILIGENCE_CLIENT_USER,
  SIDE_MENU_PAST_CLIENT_USER,
} from '../SideMenuClient/SideMenuConfig'
import { IMenuData, SIDE_MENU } from './SideMenuConfig'

export interface IRoleMenuContext {
  role: UserRole
  /** True for a client-portal user with a loaded client — picks the portal's own menus. */
  isClientPortal: boolean
  /** `null` until the client record has loaded; see the guard below. */
  isPastClient: boolean | null
  isDueDiligenceClient: boolean
  isAdminLoggedAsClient: boolean
}

/**
 * The menu a user should see.
 *
 * Extracted because three things now need it — the desktop rail, the client-portal rail,
 * and the mobile tab bar — and they must not answer differently. Previously it was a
 * `useMemo` copied into each side menu, which is exactly the arrangement that lets a rule
 * be added in one and forgotten in the other.
 *
 * Note the two config modules: `SideMenuClient/SideMenuConfig` and `SideMenu/SideMenuConfig`
 * both export a `SIDE_MENU` and a `SIDE_MENU_CLIENT_USER`, and they are *different*. Which
 * one applies is decided by `isClientPortal`, matching which rail MainLayout renders. That
 * duplication is pre-existing and worth collapsing on its own; this function at least makes
 * the choice explicit and testable rather than implicit in an import.
 */
export const resolveRoleMenuItems = ({
  role,
  isClientPortal,
  isPastClient,
  isDueDiligenceClient,
  isAdminLoggedAsClient,
}: IRoleMenuContext): IMenuData[] => {
  if (!role) {
    return []
  }

  if (role === UserRole.CLIENT_USER) {
    if (isDueDiligenceClient) {
      return isClientPortal ? SIDE_MENU_DUE_DILIGENCE_CLIENT_USER : []
    }

    // While an admin is impersonating a client, Settings would act on the admin's own
    // account rather than the client's, so it is withheld.
    const withoutSettings = (items: IMenuData[]) =>
      items.filter((item) => !isAdminLoggedAsClient || item.link !== ROUTES.SETTINGS)

    if (isPastClient) {
      return withoutSettings(SIDE_MENU_PAST_CLIENT_USER)
    }

    // `!== null` rather than truthiness: null means the client record has not loaded yet,
    // and rendering the active-client menu then would flash the wrong destinations.
    if (isPastClient !== null) {
      return withoutSettings(SIDE_MENU_CLIENT_USER)
    }

    return []
  }

  return (isClientPortal ? SIDE_MENU_CLIENT[role] : SIDE_MENU[role]) ?? []
}

export default resolveRoleMenuItems
